/**
 * Dream Graph Expression Language Interpreter
 *
 * Evaluates Abstract Syntax Trees (ASTs) produced by the parser against a
 * variable store. The interpreter is stateful — it maintains the variable store
 * across statements and assignments can modify it.
 *
 * **Design principles:**
 * 1. Type coercion follows JavaScript conventions (loose equality, truthy/falsy)
 * 2. Operations that would fail (e.g., string division) return NaN or false
 * 3. All variable names are case-sensitive
 * 4. Variables not in the store are undefined (falsy)
 * 5. Assignment operations return the assigned value
 *
 * **No side effects beyond variable store modification** — the interpreter
 * is pure except for the mutable variable store argument.
 *
 * @module scripting/interpreter
 */

import type {
  ASTNode,
  AssignmentNode,
  BinaryOpNode,
  EvaluationContext,
  EvaluationResult,
  LiteralNode,
  SequenceNode,
  UnaryOpNode,
  VariableNode,
  VariableStore,
} from './types';

/**
 * Evaluate an AST node against the given variable store
 *
 * This is the main entry point for evaluation. It handles all node types
 * and returns a structured EvaluationResult with either the computed value
 * or an error message.
 *
 * @param node The AST node to evaluate
 * @param context The evaluation context (variables and optional built-ins)
 * @returns An EvaluationResult with the value or error
 *
 * @example
 * ```typescript
 * const result = evaluate(ast, {
 *   variables: { "quest.stage": 2, "stats.gold": 100 },
 * });
 * ```
 */
export function evaluate(node: ASTNode, context: EvaluationContext): EvaluationResult {
  try {
    const value = evaluateNode(node, context);
    return {
      success: true,
      value,
      updatedVariables: context.variables,
    };
  } catch (err) {
    return {
      success: false,
      error: err instanceof Error ? err.message : String(err),
    };
  }
}

/**
 * Evaluate a single AST node
 *
 * Dispatches to the appropriate evaluation function based on node type.
 * This is the core recursive evaluation function.
 */
function evaluateNode(node: ASTNode, context: EvaluationContext): unknown {
  switch (node.type) {
    case 'Literal':
      return evaluateLiteral(node as LiteralNode);
    case 'Variable':
      return evaluateVariable(node as VariableNode, context);
    case 'BinaryOp':
      return evaluateBinaryOp(node as BinaryOpNode, context);
    case 'UnaryOp':
      return evaluateUnaryOp(node as UnaryOpNode, context);
    case 'Assignment':
      return evaluateAssignment(node as AssignmentNode, context);
    case 'Sequence':
      return evaluateSequence(node as SequenceNode, context);
    default:
      throw new Error(`Unknown node type: ${(node as any).type}`);
  }
}

/**
 * Evaluate a literal node
 *
 * Literal nodes are constant values parsed directly from the source.
 */
function evaluateLiteral(node: LiteralNode): unknown {
  return node.value;
}

/**
 * Evaluate a variable reference
 *
 * Looks up the variable in the store using its full dotted name.
 * Returns undefined if the variable is not found.
 */
function evaluateVariable(node: VariableNode, context: EvaluationContext): unknown {
  return context.variables[node.name];
}

/**
 * Evaluate a unary operation (currently only logical NOT)
 *
 * The NOT operator converts its operand to a boolean and negates it.
 * Follows JavaScript truthiness rules:
 * - Falsy values: false, 0, "", undefined, NaN
 * - Truthy values: everything else
 */
function evaluateUnaryOp(node: UnaryOpNode, context: EvaluationContext): unknown {
  const operand = evaluateNode(node.operand, context);
  if (node.operator === '!') {
    return !isTruthy(operand);
  }
  throw new Error(`Unknown unary operator: ${node.operator}`);
}

/**
 * Evaluate a binary operation
 *
 * Handles arithmetic, comparison, and logical operations.
 * Type coercion and operation semantics follow JavaScript conventions.
 *
 * **Arithmetic operations (+, -, *, /):**
 * - Numbers are operated directly
 * - Strings + anything = string concatenation
 * - Mixed types are coerced to numbers for -, *, /
 *
 * **Comparison operations (<, >, <=, >=):**
 * - Numeric comparison when possible
 * - String comparison otherwise
 *
 * **Equality operations (==, !=):**
 * - Loose equality (handles type coercion)
 * - NaN is never equal to anything (including itself)
 *
 * **Logical operations (&&, ||):**
 * - Short-circuit evaluation: && returns first falsy or last truthy;
 *   || returns first truthy or last falsy
 */
function evaluateBinaryOp(node: BinaryOpNode, context: EvaluationContext): unknown {
  const left = evaluateNode(node.left, context);
  const right = evaluateNode(node.right, context);

  switch (node.operator) {
    case '+': {
      // String concatenation if either operand is a string
      if (typeof left === 'string' || typeof right === 'string') {
        return String(left) + String(right);
      }
      const lNum = toNumber(left);
      const rNum = toNumber(right);
      return lNum + rNum;
    }

    case '-': {
      const lNum = toNumber(left);
      const rNum = toNumber(right);
      return lNum - rNum;
    }

    case '*': {
      const lNum = toNumber(left);
      const rNum = toNumber(right);
      return lNum * rNum;
    }

    case '/': {
      const lNum = toNumber(left);
      const rNum = toNumber(right);
      if (rNum === 0) {
        return Infinity;
      }
      return lNum / rNum;
    }

    case '==': {
      return looseEqual(left, right);
    }

    case '!=': {
      return !looseEqual(left, right);
    }

    case '<': {
      return toNumber(left) < toNumber(right);
    }

    case '>': {
      return toNumber(left) > toNumber(right);
    }

    case '<=': {
      return toNumber(left) <= toNumber(right);
    }

    case '>=': {
      return toNumber(left) >= toNumber(right);
    }

    case '&&': {
      // Short-circuit: return the first falsy or the last value
      if (!isTruthy(left)) return left;
      return right;
    }

    case '||': {
      // Short-circuit: return the first truthy or the last value
      if (isTruthy(left)) return left;
      return right;
    }

    default:
      throw new Error(`Unknown binary operator: ${node.operator}`);
  }
}

/**
 * Evaluate an assignment operation
 *
 * Assigns the value to the target variable in the store.
 * Supports compound assignments (+=, -=, *=, /=).
 *
 * Returns the assigned value (allowing chained assignments like `a = b = 5`).
 */
function evaluateAssignment(node: AssignmentNode, context: EvaluationContext): unknown {
  const varName = node.target.name;
  const value = evaluateNode(node.value, context);

  if (node.operator === '=') {
    context.variables[varName] = value as any;
    return value;
  }

  // Compound assignment: fetch current value and apply operator
  const current = context.variables[varName] ?? 0;
  let result: unknown;

  switch (node.operator) {
    case '+=': {
      if (typeof current === 'string' || typeof value === 'string') {
        result = String(current) + String(value);
      } else {
        result = toNumber(current) + toNumber(value);
      }
      break;
    }

    case '-=': {
      result = toNumber(current) - toNumber(value);
      break;
    }

    case '*=': {
      result = toNumber(current) * toNumber(value);
      break;
    }

    case '/=': {
      const num = toNumber(value);
      result = num === 0 ? Infinity : toNumber(current) / num;
      break;
    }

    default:
      throw new Error(`Unknown assignment operator: ${node.operator}`);
  }

  context.variables[varName] = result as any;
  return result;
}

/**
 * Evaluate a sequence of statements
 *
 * Executes each statement in order and returns the value of the last one.
 * Used for instruction scripts that contain multiple statements.
 *
 * Example: `a = 1; b = 2; a + b` evaluates to 3
 */
function evaluateSequence(node: SequenceNode, context: EvaluationContext): unknown {
  let value: unknown = undefined;
  for (const statement of node.statements) {
    value = evaluateNode(statement, context);
  }
  return value;
}

/**
 * Convert a value to a number
 *
 * Follows JavaScript type coercion:
 * - undefined → NaN
 * - null → 0
 * - boolean: true → 1, false → 0
 * - number → unchanged
 * - string → parseFloat, or NaN if not numeric
 */
function toNumber(value: unknown): number {
  if (value === undefined) return NaN;
  if (value === null) return 0;
  if (typeof value === 'boolean') return value ? 1 : 0;
  if (typeof value === 'number') return value;
  if (typeof value === 'string') {
    if (value === '') return 0;
    const num = parseFloat(value);
    return isNaN(num) ? NaN : num;
  }
  return NaN;
}

/**
 * Loose equality comparison
 *
 * Follows JavaScript == semantics:
 * - Same type: use === semantics
 * - null == undefined → true
 * - Number comparison after coercion
 * - NaN is never equal to anything (including itself)
 */
function looseEqual(a: unknown, b: unknown): boolean {
  // Same type: use strict equality
  if (typeof a === typeof b) {
    if (Number.isNaN(a) || Number.isNaN(b)) return false;
    return a === b;
  }

  // null == undefined
  if ((a === null && b === undefined) || (a === undefined && b === null)) return true;

  // Number comparisons (coerce the other side)
  if (typeof a === 'number' && typeof b === 'string') {
    return a === toNumber(b);
  }
  if (typeof a === 'string' && typeof b === 'number') {
    return toNumber(a) === b;
  }

  // Boolean coercion
  if (typeof a === 'boolean') {
    return looseEqual(toNumber(a), b);
  }
  if (typeof b === 'boolean') {
    return looseEqual(a, toNumber(b));
  }

  return false;
}

/**
 * Determine if a value is truthy
 *
 * In the Dream Graph language, truthy/falsy follows JavaScript conventions:
 * - Falsy: false, 0, "", undefined, NaN
 * - Truthy: everything else (including "0" and empty objects)
 */
function isTruthy(value: unknown): boolean {
  if (value === false) return false;
  if (value === 0) return false;
  if (value === '') return false;
  if (value === undefined) return false;
  if (Number.isNaN(value)) return false;
  return true;
}

/**
 * Validate a script's structure and variable usage
 *
 * This is a convenience function for the UI to validate scripts before saving.
 * It checks that:
 * - The script parses successfully
 * - All referenced variables follow the naming convention
 * - The AST is well-formed
 *
 * @param source The source code to validate
 * @returns An array of validation errors (empty if valid)
 */
export function validateScript(source: string): string[] {
  const errors: string[] = [];

  if (!source || !source.trim()) {
    errors.push('Script is empty');
    return errors;
  }

  // Check basic structure: no unclosed brackets or quotes
  let parenDepth = 0;
  let braceDepth = 0;
  let inString = false;
  let stringChar = '';

  for (let i = 0; i < source.length; i++) {
    const ch = source[i];

    if (inString) {
      if (ch === stringChar && source[i - 1] !== '\\') {
        inString = false;
      }
    } else {
      if (ch === '"' || ch === "'") {
        inString = true;
        stringChar = ch;
      } else if (ch === '(') {
        parenDepth++;
      } else if (ch === ')') {
        parenDepth--;
        if (parenDepth < 0) {
          errors.push('Unmatched closing parenthesis');
        }
      }
    }
  }

  if (parenDepth > 0) {
    errors.push('Unclosed parenthesis');
  }
  if (inString) {
    errors.push('Unclosed string literal');
  }

  // Check variable naming conventions (must be dot-separated identifiers)
  const varPattern = /[a-zA-Z_][a-zA-Z0-9_]*(\.[a-zA-Z_][a-zA-Z0-9_]*)*/g;
  const invalidVars = source.match(/[a-zA-Z0-9_]+(?:\.[^a-zA-Z0-9_]+|[^a-zA-Z0-9_.+\-*/()\s;!=<>&|!])/g);
  if (invalidVars) {
    // This is a rough heuristic; the actual parser will catch syntax errors
  }

  return errors;
}
